import { buildClassName } from './buildClassName';
import type { DrivingCourseOption } from '../data/courseOptions';
import type { TimeOfDayOption } from '../data/timeOfDayOptions';
import type { DrivingCameraOption, DrivingCameraMode } from '../data/drivingCameraOptions';
import type { DrivingChallengeId, DrivingChallengeOption } from '../data/challengeOptions';
import type { GraphicsQualityOption } from '../data/graphicsQualityOptions';
import { regenOptions } from '../data/regenOptions';
import type { Powertrain } from '../types';
import type {
  DrivingAssistId,
  DrivingAssists,
  RegenMode,
  WeatherMode,
} from '../model/drivingPreferences';
import {
  TUNING_CHOICES,
  type VehicleTuning,
  type VehicleTuningField,
} from '../model/vehicleTuning';

interface ChoiceOption {
  id: string;
  name: string;
  description: string;
  icon: string;
}

function ChoiceGroup<T extends ChoiceOption>({
  label,
  options,
  selectedId,
  onSelect,
  disabledIds,
  disabledNote,
}: {
  label: string;
  options: readonly T[];
  selectedId: string;
  onSelect: (id: T['id']) => void;
  /** 지금 쓸 수 없는 항목. 조용히 다른 값으로 바뀌는 것보다 비활성이 정직합니다. */
  disabledIds?: readonly string[];
  disabledNote?: string;
}) {
  return (
    <ul className="driving-choice-list" aria-label={label}>
      {options.map((option) => {
        const selected = option.id === selectedId;
        const disabled = disabledIds?.includes(option.id) ?? false;
        const description = disabled && disabledNote ? disabledNote : option.description;
        return (
          <li key={option.id}>
            <button
              type="button"
              className={buildClassName('driving-choice', 'driving-choice--selected', selected)}
              aria-pressed={selected}
              aria-label={`${option.name}: ${description}`}
              disabled={disabled}
              onClick={() => onSelect(option.id)}
            >
              <span className="driving-choice__icon" aria-hidden="true">{option.icon}</span>
              <span className="driving-choice__copy">
                <strong>{option.name}</strong>
                <small>{description}</small>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function CourseSelector(props: {
  options: readonly DrivingCourseOption[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return <ChoiceGroup label="주행 코스 선택" {...props} />;
}

export function TimeOfDaySelector(props: {
  options: readonly TimeOfDayOption[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return <ChoiceGroup label="시간대 선택" {...props} />;
}

export function DrivingCameraSelector(props: {
  options: readonly DrivingCameraOption[];
  selectedId: DrivingCameraMode;
  onSelect: (id: DrivingCameraMode) => void;
  disabledIds?: readonly string[];
  disabledNote?: string;
}) {
  return <ChoiceGroup label="주행 카메라 선택" {...props} />;
}

export function ChallengeSelector(props: {
  options: readonly DrivingChallengeOption[];
  selectedId: DrivingChallengeId;
  onSelect: (id: DrivingChallengeId) => void;
}) {
  return <ChoiceGroup label="주행 챌린지 선택" {...props} />;
}

export function GraphicsQualitySelector(props: {
  options: readonly GraphicsQualityOption[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return <ChoiceGroup label="그래픽 품질 선택" {...props} />;
}

export function RegenModeSelector({
  powertrain,
  selectedId,
  onSelect,
}: {
  powertrain: Powertrain;
  selectedId: RegenMode;
  onSelect: (id: RegenMode) => void;
}) {
  if (powertrain !== 'ev') {
    return null;
  }
  return (
    <ChoiceGroup
      label="회생 제동 단계 선택"
      options={regenOptions}
      selectedId={selectedId}
      onSelect={onSelect}
    />
  );
}

export function WeatherModeToggle({
  mode,
  onToggle,
}: {
  mode: WeatherMode;
  onToggle: () => void;
}) {
  const dynamic = mode === 'dynamic';
  return (
    <button
      type="button"
      className={buildClassName('wide-setting-toggle', 'wide-setting-toggle--enabled', dynamic)}
      aria-pressed={dynamic}
      onClick={onToggle}
    >
      <span><strong>동적 날씨</strong><small>날씨가 주행 중 점진적으로 변합니다</small></span>
      <span aria-hidden="true">{dynamic ? 'ON' : 'OFF'}</span>
    </button>
  );
}

const ASSIST_LABELS: ReadonlyArray<{ id: DrivingAssistId; name: string; description: string }> = [
  { id: 'abs', name: 'ABS', description: '제동 중 바퀴 잠김 방지' },
  { id: 'tcs', name: 'TCS', description: '가속 시 휠스핀 억제' },
  { id: 'esc', name: 'ESC', description: '차체 자세와 회전 안정화' },
];

export function DriverAssistToggles({
  assists,
  onToggle,
}: {
  assists: DrivingAssists;
  onToggle: (assist: DrivingAssistId) => void;
}) {
  return (
    <div className="assist-toggle-list" role="group" aria-label="주행 보조 설정">
      {ASSIST_LABELS.map((assist) => (
        <button
          key={assist.id}
          type="button"
          className={buildClassName('assist-toggle', 'assist-toggle--enabled', assists[assist.id])}
          aria-pressed={assists[assist.id]}
          aria-label={`${assist.name}: ${assist.description}`}
          onClick={() => onToggle(assist.id)}
        >
          <span><strong>{assist.name}</strong><small>{assist.description}</small></span>
          <span className="assist-toggle__state" aria-hidden="true">{assists[assist.id] ? 'ON' : 'OFF'}</span>
        </button>
      ))}
    </div>
  );
}

const TUNING_LABELS: Record<VehicleTuningField, string> = {
  tire: '타이어',
  suspension: '서스펜션',
  gearing: '기어비',
  brakes: '브레이크',
};

export function VehicleTuningControls({
  tuning,
  onSelect,
  onReset,
}: {
  tuning: VehicleTuning;
  onSelect: (field: VehicleTuningField, value: string) => void;
  onReset: () => void;
}) {
  return (
    <div className="vehicle-tuning" aria-label="차량 튜닝">
      {(Object.keys(TUNING_CHOICES) as VehicleTuningField[]).map((field) => (
        <div key={field} className="vehicle-tuning__row">
          <span>{TUNING_LABELS[field]}</span>
          <div role="group" aria-label={TUNING_LABELS[field]}>
            {TUNING_CHOICES[field].map((choice) => (
              <button
                key={choice.id}
                type="button"
                aria-pressed={tuning[field] === choice.id}
                className={buildClassName(
                  'vehicle-tuning__choice',
                  'vehicle-tuning__choice--selected',
                  tuning[field] === choice.id
                )}
                onClick={() => onSelect(field, choice.id)}
              >
                {choice.name}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button type="button" className="vehicle-tuning__reset" onClick={onReset}>기본값 복원</button>
    </div>
  );
}
